import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  fetchCategories,
  createCategory,
  updateCategory,
  deleteCategory,
} from "@/store/altingross/categories";
import PageHeader from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Loader2, Tag } from "lucide-react";
import { toast } from "sonner";

// ─── Validation ──────────────────────────────────────────────────────────────
const schema = Yup.object({
  title: Yup.string().trim().min(2, "En az 2 karakter").required("Başlık zorunlu"),
});

const EMPTY = { title: "" };
const PAGE_SIZE = 20;

export default function Categories() {
  const dispatch = useDispatch();
  const { list, loading, pagination, savingId, deletingId } = useSelector(
    (s) => s.categories
  );

  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // ─── Load ──────────────────────────────────────────────────────────────────
  const load = useCallback(
    (p = page) => dispatch(fetchCategories({ page: p, size: PAGE_SIZE })),
    [dispatch, page]
  );

  useEffect(() => { load(page); }, [page]); // eslint-disable-line

  // ─── Form ──────────────────────────────────────────────────────────────────
  const formik = useFormik({
    initialValues: EMPTY,
    validationSchema: schema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const payload = { title: values.title.trim() };
      let res;
      if (editing) {
        res = await dispatch(updateCategory({ id: editing.id, data: payload }));
        if (!res.error) {
          toast.success("Kategori güncellendi.");
          handleClose();
          load(page);
        } else {
          toast.error("Güncelleme başarısız.");
        }
      } else {
        res = await dispatch(createCategory(payload));
        if (!res.error) {
          toast.success("Kategori oluşturuldu.");
          handleClose();
          load(1);
          setPage(1);
        } else {
          toast.error("Oluşturma başarısız.");
        }
      }
    },
  });

  const handleOpen = (cat = null) => {
    setEditing(cat);
    formik.resetForm({ values: cat ? { title: cat.title } : EMPTY });
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setEditing(null);
    formik.resetForm({ values: EMPTY });
  };

  // ─── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget) return;
    const res = await dispatch(deleteCategory(deleteTarget.id));
    if (!res.error) {
      toast.success("Kategori silindi.");
      load(page);
    } else {
      toast.error("Silme başarısız.");
    }
    setDeleteTarget(null);
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        testId="categories-header"
        title="Kategori Tanımları"
        description="Ürün ve içerik kategorilerini yönetin."
        action={
          <Button size="sm" onClick={() => handleOpen()}>
            <Plus className="w-4 h-4 mr-1" />
            Yeni Ekle
          </Button>
        }
      />

      {/* List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        {/* Table header */}
        <div className="grid grid-cols-[1fr_auto_auto] items-center border-b border-gray-100 px-4 py-2.5">
          <span className="text-xs font-semibold text-black uppercase tracking-wide">Başlık</span>
          <span className="text-xs font-semibold text-black uppercase tracking-wide hidden sm:block pr-8">Oluşturulma</span>
          <span></span>
        </div>

        {loading && list.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Yükleniyor…
          </div>
        ) : list.length === 0 ? (
          <div className="py-12 text-center text-slate-400 text-sm">
            Henüz kategori eklenmedi.
          </div>
        ) : (
          <ul>
            {list.map((cat, idx) => (
              <li
                key={cat.id}
                className={`grid grid-cols-[1fr_auto_auto] items-center px-4 py-2.5 gap-4 ${
                  idx !== list.length - 1 ? "border-b border-gray-100" : ""
                } hover:bg-gray-50 transition-colors`}
              >
                <span className="flex items-center gap-2 text-sm text-gray-800 font-medium truncate">
                  <Tag className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
                  {cat.title}
                </span>
                <span className="text-xs text-gray-400 hidden sm:block whitespace-nowrap">
                  {cat.createdAt
                    ? new Date(cat.createdAt).toLocaleDateString("tr-TR")
                    : "—"}
                </span>
                <span className="flex items-center gap-1">
                  {deletingId === cat.id ? (
                    <Loader2 className="w-4 h-4 animate-spin text-red-400" />
                  ) : (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-gray-400 hover:text-gray-700"
                        onClick={() => handleOpen(cat)}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-gray-400 hover:text-red-500"
                        onClick={() => setDeleteTarget(cat)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}

        {/* Pagination */}
        {list.length > 0 && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 text-xs text-gray-400">
            <span>
              Toplam {pagination.count} kayıt — Sayfa {pagination.page}/{pagination.totalPages || 1}
            </span>
            <div className="flex gap-1.5">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={!pagination.hasPrev}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Önceki
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={!pagination.hasNext}
                onClick={() => setPage((p) => p + 1)}
              >
                Sonraki
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={modalOpen} onOpenChange={(o) => { if (!o) handleClose(); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editing ? "Kategoriyi Düzenle" : "Yeni Kategori"}</DialogTitle>
            <DialogDescription>
              {editing
                ? `"${editing.title}" kategorisini güncelle.`
                : "Yeni bir kategori oluştur."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4 pt-1">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cat-title">Başlık</Label>
              <Input
                id="cat-title"
                name="title"
                placeholder="Kategori adı"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                autoFocus
              />
              {formik.touched.title && formik.errors.title && (
                <p className="text-xs text-red-400">{formik.errors.title}</p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                İptal
              </Button>
              <Button
                type="submit"
                disabled={savingId !== null || formik.isSubmitting}
              >
                {savingId !== null ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Kaydediliyor…</>
                ) : editing ? "Güncelle" : "Oluştur"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Alert */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kategoriyi sil</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteTarget?.title}</strong> kategorisi kalıcı olarak silinecek. Emin misiniz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-500 hover:bg-red-600"
              onClick={handleDelete}
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
